import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';

import { types as customerListsTypes, actions as customerListsActions } from '@src/ducks/customerLists';
import {
    fetchCustomerLists,
    createCustomerList,
    updateCustomerList,
    deleteCustomerList,
} from '@src/services/customerListsService';
import { handleSagaFailure } from '@src/utils/sagaHelpers';

/**
 * Watch for customer lists related actions
 */
export function * watchCustomerLists() {
    yield takeLatest(customerListsTypes.CUSTOMER_LISTS_GET, onGetCustomerLists);
    yield takeEvery(customerListsTypes.CUSTOMER_LIST_CREATE, onCreateCustomerList);
    yield takeEvery(customerListsTypes.CUSTOMER_LIST_UPDATE, onUpdateCustomerList);
    yield takeEvery(customerListsTypes.CUSTOMER_LIST_DELETE, onDeleteCustomerList);
}

/**
 * Saga to handle fetching customer lists
 */
function * onGetCustomerLists({ payload }) {
    try {
        const { premierAccessGroupId, params } = payload;

        if (!premierAccessGroupId) {
            throw new Error('Premier Access Group ID is required');
        }

        const response = yield call(fetchCustomerLists, premierAccessGroupId, params);

        yield put(customerListsActions.getCustomerListsSuccess(response));
    } catch (error) {
        yield put(customerListsActions.getCustomerListsFailure(error.message || 'Failed to fetch customer lists'));
        yield call(handleSagaFailure, error);
    }
}

/**
 * Saga to handle creating a new customer list
 */
function * onCreateCustomerList({ payload }) {
    try {
        const response = yield call(createCustomerList, payload);
        
        let list = response;
        if (list && list.data) list = list.data;
        if (list && list.customer_list) list = list.customer_list;
        
        yield put(customerListsActions.createCustomerListSuccess(list));

        // Optionally show success notification
        // yield call(showNotification, 'Customer list created successfully', 'success');
    } catch (error) {
        yield put(customerListsActions.createCustomerListFailure(error.message || 'Failed to create customer list'));
        yield call(handleSagaFailure, error);
    }
}

/**
 * Saga to handle updating a customer list
 */
function * onUpdateCustomerList({ payload }) {
    try {
        const { listId, listData } = payload;

        const response = yield call(updateCustomerList, listId, listData);

        yield put(customerListsActions.updateCustomerListSuccess(response));

        // Optionally show success notification
        // yield call(showNotification, 'Customer list updated successfully', 'success');
    } catch (error) {
        yield put(customerListsActions.updateCustomerListFailure(error.message || 'Failed to update customer list'));
        yield call(handleSagaFailure, error);
    }
}

/**
 * Saga to handle deleting a customer list
 */
function * onDeleteCustomerList({ payload }) {
    try {
        const { listId } = payload;

        yield call(deleteCustomerList, listId);

        yield put(customerListsActions.deleteCustomerListSuccess(listId));

        // Optionally show success notification
        // yield call(showNotification, 'Customer list deleted successfully', 'success');
    } catch (error) {
        yield put(customerListsActions.deleteCustomerListFailure(error.message || 'Failed to delete customer list'));
        yield call(handleSagaFailure, error);
    }
}
